import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as BackButton } from "../img/bacbuttonicon.svg";
import { instance2 } from "../utils/axiosInstance";
import DatePicker from "react-datepicker";
import CalendarIcon from "../img/calendaricon.svg";
import BgnIcon from "../img/logobgn250.png";
import { basecode } from "../mainscreen/pesananscreen/baserobotocode";
import { jsPDF } from "jspdf"; // Import jsPDF for PDF generation
import "jspdf-autotable"; // Import the autotable plugin for jsPDF
import { formatNumber } from "../numberingformat"; // Ensure formatNumber works for formatting
import QRCode from "qrcode";
import { basecodeBold } from "./pesananscreen/baserobotoboldcode";
import { padTimeUnit } from "src/lib/utils";

const getDayName = (d) =>
  new Date(d).toLocaleDateString("id-ID", { weekday: "long" });

// Helper function to safely convert values to integers
const toInt = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  if (Number.isFinite(n)) return n;
  const s = String(v).replace(/[^\d-]/g, "");
  const p = parseInt(s, 10);
  return Number.isFinite(p) ? p : 0;
};

// Category IDs for fetching ratings
const CATEGORY_ID_BY_KEY = {
  karbohidrat: 1,
  protein: 2,
  sayuran: 3,
  buah: 4,
  minuman: 5,
};

// Fetches detailed ratings for a specific order result.
const fetchRatingsForResult = async (resultId) => {
  try {
    const res = await instance2.get("/customers/result_rate_detail", {
      params: { param: "result_id", id: resultId },
    });
    let rows = Array.isArray(res?.data) ? res.data : [];
    rows = rows.sort((a, b) => a.id - b.id);

    const catToKey = Object.fromEntries(
      Object.entries(CATEGORY_ID_BY_KEY).map(([k, cid]) => [cid, k]),
    );

    const byMenu = {};
    for (const r of rows) {
      const menuKey = catToKey[r.category_id];
      if (!menuKey) continue;
      if (!byMenu[menuKey]) byMenu[menuKey] = {};
      byMenu[menuKey][r.param_id] = r.value;
    }
    return byMenu;
  } catch (e) {
    console.error("Failed to fetch result_rate_detail:", e);
    return {};
  }
};

// Gets the natural dimensions of an image from its data URL.
const getImageDims = (dataUrl) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.width, h: img.height });
    img.onerror = () => resolve({ w: 1000, h: 1000 }); // fallback
    img.src = dataUrl;
  });

// Turn a Blob into a data URL (base64) that jsPDF can embed
const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });

// Robustly fetch feedback images for a result and return data URLs
const fetchFeedbackImageDataURLs = async (resultId) => {
  // 1) Get rows that have an image reference (image_id or image path)
  const res = await instance2.get(`/customers_review/feedback/${resultId}`);
  const rows = (res?.data?.data ?? res?.data ?? []).filter(
    (r) => !!r?.image_id || !!r?.image,
  );

  if (!rows.length) return [];

  // 2) Fetch each image as Blob; guard against interceptors that unwrap to Blob directly
  const blobs = await Promise.all(
    rows.map(async (row) => {
      const url = row.image_id ? `/api/image/${row.image_id}` : row.image;
      const r = await instance2.get(url, {
        responseType: "blob",
        transformResponse: [(d) => d], // keep raw
      });
      return r?.data ?? r; // if interceptor already returned Blob
    }),
  );

  // 3) Convert to data URLs
  return Promise.all(blobs.map((b) => blobToDataURL(b)));
};

const appendEvaluationPage = async (
  doc,
  orderData,
  dataMenu,
  iconBase64,
  dateStr,
  qtyValue,
  deliveryLabel,
  kitchenData,
) => {
  doc.addPage();
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const centerX = pageWidth / 2;
  const resultId = orderData[`result_id_${deliveryLabel}`.toLowerCase()];

  const resultResponse = await instance2.get(
    `/customers_result/id/${resultId}`,
  );
  const customersResult = resultResponse.data[0];

  const addHeader = () => {
    const headerStartY = margin / 2;
    const iconSize = 80;
    const iconX = margin;
    const iconY = headerStartY;

    if (iconBase64) {
      doc.addImage(iconBase64, "PNG", iconX, iconY, iconSize, iconSize);
    }

    const addressLines = [
      "BADAN GIZI NASIONAL",
      "SATUAN PELAYANAN PEMENUHAN GIZI (SPPG)",
      "KECAMATAN ARCAMANIK",
    ];

    let textY = headerStartY + 28;
    doc.setFont("Roboto", "bold");
    doc.setFontSize(10);
    addressLines.forEach((line) => {
      doc.text(line, centerX, textY, { align: "center" });
      textY += 12;
    });

    doc.setFont("Roboto", "normal");
    doc.setFontSize(9);
    const addressDetail =
      "Jl. A.H. Nasution No. 114, Kel Cisaranten Bina Harapan, Kec. Arcamanik";
    doc.text(kitchenData[0]?.address || "", centerX, textY, { align: "center" });
    textY += 10;

    // Blue separator line
    const lineY = textY + 20;
    doc.setLineWidth(2);
    doc.setDrawColor(100, 149, 237);
    doc.line(margin, lineY, pageWidth - margin, lineY);
    doc.setDrawColor(0);

    textY = lineY + 20;
    doc.setFont("Roboto", "bold");
    doc.setFontSize(10);
    const titleText = "EVALUASI HARIAN MAKAN BERGIZI GRATIS";
    doc.text(titleText, centerX, textY, { align: "center" });

    const textWidth = doc.getTextWidth(titleText);
    const underlineY = textY + 3;
    doc.setLineWidth(0.5);
    doc.line(
      centerX - textWidth / 2,
      underlineY,
      centerX + textWidth / 2,
      underlineY,
    );

    return underlineY + 24;
  };

  let yOffset = addHeader();

  doc.setFont("Roboto", "normal");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);

  let dayName = getDayName(new Date(dataMenu.date));
  try {
    const d = new Date(orderData?.dateISO || orderData?.orderDate);
    if (!isNaN(d)) dayName = d.toLocaleDateString("id-ID", { weekday: "long" });
  } catch (_) {}

  const details = [
    ["Hari", dayName],
    ["Tanggal", dateStr],
    ["Instansi", orderData?.customer_name || "Badan Gizi Nasional"],
    ["Sesi Makan", deliveryLabel || ""],
    [
      "Nama Kader Penanggungjawab",
      orderData[`recived_by_${deliveryLabel}`.toLowerCase()] || "",
    ],
  ];

  const maxLabelW = Math.max(
    ...details.map(([label]) => doc.getTextWidth(label)),
  );
  const colonX = margin + maxLabelW + 8;
  const valueX = colonX + 8;

  details.forEach(([label, value]) => {
    doc.setFont("Roboto", "normal");
    doc.text(label, margin, yOffset);
    doc.text(":", colonX, yOffset);
    doc.text(String(value), valueX, yOffset);
    yOffset += 16;
  });

  yOffset += 12;

  doc.setFont("Roboto", "bold");
  doc.text("A. Daya Terima Sasaran", margin, yOffset);
  yOffset += 6;

  const tableBodyA = [
    [
      "1",
      `${Number(qtyValue || 0)} ompreng`,
      `${formatNumber(orderData[`received_qty_${deliveryLabel}`.toLowerCase()])} ompreng` ||
        "",
      `a. Jumlah ompreng yang habis: ${customersResult?.omprenghabis ? formatNumber(customersResult.omprenghabis) : "-"}\nb. Jumlah ompreng yang tersisa: ${customersResult?.omprengsisa ? formatNumber(customersResult.omprengsisa) : "-"}`,
    ],
  ];

  doc.setFont("Roboto", "normal");
  doc.autoTable({
    startY: yOffset,
    theme: "grid",
    head: [
      [
        { content: "No", styles: { halign: "center" } },
        { content: "Jumlah Pengiriman", styles: { halign: "center" } },
        { content: "Jumlah Diterima", styles: { halign: "center" } },
        { content: "Keterangan", styles: { halign: "center" } },
      ],
    ],
    body: tableBodyA,
    margin: { left: 48, right: 48 },
    styles: {
      font: "Roboto",
      fontSize: 10,
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
    },
    columnStyles: {
      0: { halign: "center" },
      1: { halign: "center" },
      2: { halign: "center" },
    },
  });
  yOffset = doc.lastAutoTable.finalY + 28;

  doc.setFont("Roboto", "bold");
  doc.text("B. Uji Cita Rasa (Oleh Kader)", margin, yOffset);
  yOffset += 6;

  const ratingsByMenu = await fetchRatingsForResult(resultId);
  const menuOrder = ["karbohidrat", "protein", "sayuran", "buah", "minuman"];
  const tableBodyB = menuOrder
    .filter((key) => dataMenu?.[key.toUpperCase()] !== null)
    .map((key, idx) => {
      const vals = ratingsByMenu[key] || {};
      return [
        String(idx + 1),
        dataMenu?.[key.toUpperCase()],
        vals[1] ?? "", // Warna
        vals[2] ?? "", // Aroma
        vals[3] ?? "", // Tekstur
        vals[4] ?? "", // Rasa
        vals[5] ?? "", // Kematangan
      ];
    });

  doc.autoTable({
    startY: yOffset,
    theme: "grid",
    head: [
      [
        {
          content: "No",
          rowSpan: 2,
          styles: { valign: "middle", halign: "center" },
        },
        {
          content: "Nama Menu",
          rowSpan: 2,
          styles: { valign: "middle", halign: "center" },
        },
        {
          content: "Komponen Organoleptik",
          colSpan: 5,
          styles: { halign: "center" },
        },
      ],
      ["Warna", "Aroma", "Tekstur", "Rasa", "Kematangan"],
    ],
    body: tableBodyB,
    margin: { left: 48, right: 48 },
    styles: {
      font: "Roboto",
      fontSize: 10,
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      halign: "center",
    },
    columnStyles: {
      0: { halign: "center" },
      1: { halign: "left" },
      2: { halign: "center" },
      3: { halign: "center" },
      4: { halign: "center" },
      5: { halign: "center" },
      6: { halign: "center" },
    },
  });

  doc.setFontSize(9);
  doc.setFont("Roboto", "normal");
  doc.text(
    "*Keterangan: 1 = Sangat Tidak Suka, 2 = Tidak Suka, 3 = Netral, 4 = Suka, 5 = Sangat Suka",
    margin,
    doc.lastAutoTable.finalY + 12,
  );
  yOffset = doc.lastAutoTable.finalY + 24;

  doc.autoTable({
    startY: yOffset,
    theme: "grid",
    head: [["Saran Dan Masukan"]],
    body: [[customersResult?.feedback || ""]],
    margin: { left: 48, right: 48 },
    styles: {
      font: "Roboto",
      fontSize: 10,
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      halign: "center",
    },
    bodyStyles: { minCellHeight: 100 },
  });
  yOffset = doc.lastAutoTable.finalY + 32;

  doc.setFont("Roboto", "normal");
  doc.setFontSize(10);

  const signatureLabel = customersResult?.received_by || "";
  const labelWidth = doc.getTextWidth(signatureLabel);
  const lineWidth = Math.max(150, labelWidth + 20);

  const signatureX = pageWidth - margin - lineWidth / 2;
  const signatureY = yOffset + 10;

  doc.text("Penerima", signatureX, signatureY, { align: "center" });
  doc.line(
    signatureX - lineWidth / 2,
    signatureY + 56,
    signatureX + lineWidth / 2,
    signatureY + 58,
  );
  doc.text(signatureLabel, signatureX, signatureY + 56 + 15, {
    align: "center",
  });

  try {
    const imageDataUrls = await fetchFeedbackImageDataURLs(resultId);
    if (imageDataUrls.length > 0) {
      doc.addPage();
      doc.setFont("Roboto", "bold");
      doc.setFontSize(12);
      doc.text("Lampiran Foto", margin, margin);

      const pageH = doc.internal.pageSize.getHeight();
      const gapY = 20;
      const availW = pageWidth - margin * 2;
      const availH = pageH - margin * 2 - 20;
      const slotH = (availH - gapY) / 2;

      for (let i = 0; i < imageDataUrls.length; i++) {
        if (i > 0 && i % 2 === 0) {
          doc.addPage();
          doc.setFont("Roboto", "bold");
          doc.setFontSize(12);
          doc.text("Lampiran Foto", margin, margin);
        }

        const dataUrl = imageDataUrls[i];
        const fmt = dataUrl.startsWith("data:image/jpeg") ? "JPEG" : "PNG";
        const slotIdx = i % 2;
        const slotTopY = margin + 20 + slotIdx * (slotH + gapY);

        const { w: natW, h: natH } = await getImageDims(dataUrl);
        const scale = Math.min(availW / natW, slotH / natH);
        const drawW = natW * scale;
        const drawH = natH * scale;

        const x = margin + (availW - drawW) / 2;
        const y = slotTopY + (slotH - drawH) / 2;

        doc.addImage(dataUrl, fmt, x, y, drawW, drawH);
      }
    }
  } catch (e) {
    console.error("Failed to append images to PDF:", e);
  }
};

export default function TambahHasilProduksi() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const holidayId = queryParams.get("id");

  const startDate = queryParams.get("date");
  const kitchenIdKoperasi = queryParams.get("kitchenId");

  const [selectedDate, setSelectedDate] = useState(
    startDate ? new Date(startDate) : new Date(),
  );
  const [loading, setLoading] = useState(!!holidayId); // Load only if editing
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState([]);
  const [kitchenData, setKitchenData] = useState([]);
  const [dataMenu, setDataMenu] = useState([]);
  const [dataCustomers, setDataCustomers] = useState([]);
  console.log(dataCustomers, "dataCustomers");
  const storedUserId = localStorage.getItem("userAuthentication");
  const roleId = parseInt(localStorage.getItem("roleId"), 10);
  const storedKitchenId = localStorage.getItem("kitchenId");
  const storedLocationId = localStorage.getItem("locationId");
  const storedKitchenName = localStorage.getItem("kitchenname");
  const storedLocationName = localStorage.getItem("locationname");
  const initialFormData = {
    date: new Date(),
    kitchen_id: parseInt(storedKitchenId, 10),
    qty_sch: "",
    qty_real: "",
    location_id: parseInt(storedLocationId, 10),
    admin_id: storedUserId,
  };
  const [kitchenName, setKitchenName] = useState(storedKitchenName || "");
  const [isMenuSaved, setIsMenuSaved] = useState(false);
  const [checkingMenuStatus, setCheckingMenuStatus] = useState(true);

  useEffect(() => {
    const checkMenuSavedStatus = async () => {
      try {
        setCheckingMenuStatus(true);
        const response = await instance2.get(
          `/saved_menu_schedule/kitchen/${storedKitchenId || kitchenIdKoperasi}/${localDateString(selectedDate)}`,
        );
        console.log("Menu Saved Status Response:", response.data);
        // Check if saved === "y"
        setIsMenuSaved(response.data[0].saved === "Y");
      } catch (err) {
        console.error("Failed to check menu saved status:", err);
        setIsMenuSaved(false);
      } finally {
        setCheckingMenuStatus(false);
      }
    };

    checkMenuSavedStatus();
  }, [storedKitchenId, kitchenIdKoperasi, selectedDate]);

  useEffect(() => {
    const fetchKitchenName = async () => {
      if (!storedKitchenName && kitchenIdKoperasi && storedLocationId) {
        try {
          const response = await instance2.get("/kitchen");
          const kitchens = response.data;

          const matched = kitchens.find(
            (k) =>
              String(k.kitchen_id) === String(kitchenIdKoperasi) &&
              String(k.location_id) === String(storedLocationId),
          );

          if (matched) {
            setKitchenName(matched.kitchen_name);
            setKitchenData(matched);
            localStorage.setItem("kitchenname", matched.kitchen_name); // optional
          }
        } catch (err) {
          console.error("Failed to fetch kitchen:", err);
        }
      }
    };

    fetchKitchenName();
  }, [storedKitchenName, kitchenIdKoperasi, storedLocationId]);

  useEffect(() => {
    const fetchKitchenName = async () => {
        try {
          const response = await instance2.get(`/kitchen/${storedKitchenId || kitchenIdKoperasi}`);
          const kitchens = response.data;

            setKitchenData(kitchens);
            console.log(kitchens, "kitchens");
        } catch (err) {
          console.error("Failed to fetch kitchen:", err);
        }
    };

    fetchKitchenName();
  }, []);
  
  // Prepopulate formData with dataCustomers

  const [orderData, setOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const id = localStorage.getItem("kitchenId");
  const FEEDBACK_WINDOW_HOURS = 3;

  const fetchData = useCallback(async () => {
    if (!id) {
      //setError("Informasi mengenai ID pesanan tidak ditemukan.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    //setError(null);
    setOrderData(null);

    try {
      const resultResponse = await instance2.get(`/customers_result/id/${id}`);

      if (!resultResponse || resultResponse.data.length === 0) {
        //setError("Kami tidak dapat menemukan pesanan yang Anda cari.");
        setIsLoading(false);
        return;
      }

      const fetchedOrderData = resultResponse.data[0];

      const receivedDate = new Date(fetchedOrderData.date);
      const [receivedHours, receivedMinutes] = fetchedOrderData.received_time
        ? fetchedOrderData.received_time
            .split(":")
            .map((timeStr) => Number(timeStr))
        : [0, 0];

      const feedbackDeadlineTimestamp = fetchedOrderData.received_time
        ? new Date(fetchedOrderData.date).setHours(
            receivedHours + FEEDBACK_WINDOW_HOURS,
            receivedMinutes,
            0,
            0,
          )
        : null;

      const formattedReceivedDate = receivedDate.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });

      const formattedRcvTime = fetchedOrderData.received_time
        ? `${padTimeUnit(receivedHours)}.${padTimeUnit(receivedMinutes)}`
        : "";

      setOrderData({
        ...fetchedOrderData,
        hasSubmittedReport: fetchedOrderData.received_qty !== null,
        dateReceivedObj: receivedDate,
        formattedDate: formattedReceivedDate,
        feedbackDeadline: feedbackDeadlineTimestamp,
        formattedReceivedTime: formattedRcvTime,
      });
    } catch (err) {
      console.error("Error fetching data:", err);
      //setError(err.message || "Terjadi kesalahan saat mengambil data.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  console.log("Order Data:", orderData); // Debug log

  const [formData, setFormData] = useState(initialFormData);

  const localDateString = (thedate) => {
    const year = thedate.getFullYear();
    const month = String(thedate.getMonth() + 1).padStart(2, "0"); // Adding 1 because months are 0-based
    const day = String(thedate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await instance2.get(
          `/kitchen_result/${storedKitchenId || kitchenIdKoperasi}/${localDateString(selectedDate)}`,
        );
        console.log("API Response:", response.data);
        setData(response.data); // Store API data
      } catch (err) {
        setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, storedKitchenId || kitchenIdKoperasi]);

  useEffect(() => {
    const fetchDataCustomers = async () => {
      try {
        setLoading(true);
        const response = await instance2.get(
          `/customers/result/${storedKitchenId || kitchenIdKoperasi}/${localDateString(selectedDate)}`,
        );
        const rows = Array.isArray(response.data) ? response.data : [];

        // 🔧 Normalize once: keep numbers as numbers
        const normalized = rows.map((r) => ({
          ...r,
          qty_sch: toInt(r.qty_sch),
          qty_sch_pagi: toInt(r.qty_sch_pagi),
          qty_sch_siang: toInt(r.qty_sch_siang),
          qty_real: toInt(r.qty_real),
          qty_real_pagi: toInt(r.qty_real_pagi),
          qty_real_siang: toInt(r.qty_real_siang),
        }));

        setDataCustomers(normalized);
      } catch (err) {
        setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchDataCustomers();
  }, [storedKitchenId || kitchenIdKoperasi, selectedDate]);

  useEffect(() => {
    const fetchDataMenu = async () => {
      try {
        setLoading(true);
        const response = await instance2.get(
          `/menu_schedule/kitchen/${storedKitchenId || kitchenIdKoperasi}/${localDateString(selectedDate)}/${localDateString(selectedDate)}`,
        );
        console.log("Menu API Response:", response.data[0]);
        setDataMenu(response.data[0]); // Store API data

        // sayur = dataMenu[0].KARBOHIDRAT
        // buah = dataMenu[0].KARBOHIDRAT
        // minuman = dataMenu[0].KARBOHIDRAT
      } catch (err) {
        setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchDataMenu();
  }, [storedLocationId, selectedDate]); // ✅ Refetch when `selectedDate` changes

  useEffect(() => {
    if (data.length > 0) {
      const storedQty_sch = parseInt(data[0].qty_sch, 10); // Convert to integer safely
      const storedQty_real = parseInt(data[0].qty_real, 10); // Convert to integer safely

      console.log("Stored Qty_sch:", storedQty_sch);

      setFormData((prev) => ({
        ...prev,
        qty_sch: storedQty_sch, // ✅ Always update qty_sch
        qty_real: storedQty_real, // ✅ Reset qty_real when date changes
      }));
    }
  }, [data]); // ✅ Runs when `data` updates

  useEffect(() => {
    if (
      formData.qty_real === "" ||
      formData.qty_real === null ||
      formData.qty_real === undefined
    ) {
      setFormData((prev) => ({
        ...prev,
        qty_real: prev.qty_sch, // Set qty_real to qty_sch only if it's empty
      }));
    }
  }, [formData.qty_sch]); // ✅ Runs when qty_sch updates

  // track per-row per-session PDF download state
  const [pdfStatus, setPdfStatus] = useState({});
  const pdfKey = (id, sess) => `${id}-${sess}-pdf`;
  const isPdfLoading = (id, sess) => pdfStatus[pdfKey(id, sess)] === "loading";

  const downloadOnePDF = async (row, session) => {
    const key = pdfKey(row.id, session);
    setPdfStatus((s) => ({ ...s, [key]: "loading" }));

    const qty =
      session === "pagi"
        ? toInt(formData[`qty_real_pagi_${row.id}`] ?? getBasePagi(row))
        : toInt(formData[`qty_real_siang_${row.id}`] ?? getBaseSiang(row));

    const label = session === "pagi" ? del1Label : del2Label;
    const qr = session === "pagi" ? row.qr_pagi : row.qr_siang;

    try {
      if (qty > 0) {
        await generatePDFBaru(
          row, // 👈 This already has all the data!
          row.customer_name,
          selectedDate,
          qty,
          label,
          qr,
          session, // 👈 ADD THIS
        );
      }
    } finally {
      setPdfStatus((s) => ({ ...s, [key]: "idle" }));
    }
  };

  const generatePDFBaru = async (
    item,
    schoolName,
    orderDate,
    qtyValue,
    deliveryLabel = "",
    qrCode = null,
    session = "pagi", // 👈 ADD THIS to know which session
  ) => {
    console.log(item, "item");
    console.log(orderDate);
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 48;
    const pageWidth = doc.internal.pageSize.getWidth();

    // Fonts
    doc.addFileToVFS("Roboto-Regular.ttf", basecode);
    doc.addFileToVFS("Roboto-Bold.ttf", basecodeBold);
    doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
    doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");
    doc.setFont("Roboto", "normal");

    const receivedQty =
      session === "pagi" ? item.received_qty_pagi : item.received_qty_siang;

    const receivedTime =
      session === "pagi" ? item.received_time_pagi : item.received_time_siang;

    const receivedBy =
      session === "pagi" ? item.recived_by_pagi : item.recived_by_siang;

    const receivedPos =
      session === "pagi" ? item.received_pos_pagi : item.received_pos_siang;

    const receivedPhone =
      session === "pagi" ? item.received_phone_pagi : item.received_phone_siang;

    // Helpers
    const fmtDate = (d) => {
      const date = new Date(d);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    // helper: tanda tangan dengan ruang kosong di atas
    const signatureLineBox = (label, x, y, w = 180, h = 90) => {
      doc.setFontSize(10);
      doc.text(label, x, y);
      const lineY = y + h;
      doc.line(x, lineY, x + w, lineY);
      return y + h + 20; // pointer ke bawah garis + sedikit jarak ekstra
    };

    // Replaces your current drawCenteredTitle
    const drawCenteredTitle = (text, y, iconBase64) => {
      const lineHeight = 14;
      const logoSize = 72;
      const gap = 10;

      const textAreaX = margin + logoSize + gap;
      const textAreaW = pageWidth - margin * 2 - logoSize - gap;

      doc.setFont("Roboto", "bold");
      doc.setFontSize(12);

      const lines = doc.splitTextToSize(text, textAreaW);
      const xCenter = textAreaX + textAreaW / 2;

      lines.forEach((line, idx) => {
        doc.text(line, xCenter, y + idx * lineHeight, { align: "center" });
      });

      const textBlockHeight = (lines.length - 1) * lineHeight;
      const logoY = y - logoSize / 2 + textBlockHeight / 2;

      if (iconBase64) {
        doc.addImage(iconBase64, "PNG", margin, logoY, logoSize, logoSize);
      }

      doc.setFont("Roboto", "normal");
      return y + lines.length * lineHeight + 8;
    };

    const drawParagraph = (text, y) => {
      doc.setFontSize(10);
      const w = pageWidth - margin * 2;
      const lines = doc.splitTextToSize(text, w);
      doc.text(lines, margin, y);
      return y + lines.length * 14;
    };

    // Signature box with header & bottom captions INSIDE the rectangle
    // Signature box with header (top), and wrapped name/jabatan at the bottom
    const signatureBoxInside = (
      x,
      y,
      w = 180,
      h = 90,
      {
        header = "Mengetahui:",
        name = kitchenData[0]?.kitchen_head || "",
        jabatan = "",
        padding = { top: 10, right: 6, bottom: 0, left: 6 },
        headerNameGap = 38, // min gap from header baseline to *top* of name block
        nameJabatanGap = 5, // gap between name block and jabatan block
        fontSize = 10,
      } = {},
    ) => {
      // Box
      doc.setLineWidth(0.5);
      doc.rect(x, y, w, h);

      // Header
      doc.setFont("Roboto", "bold");
      doc.setFontSize(fontSize);
      const headerX = x + padding.left;
      const headerY = y + padding.top; // header baseline
      doc.text(header, headerX, headerY, { align: "left" });

      // Prepare wrapping
      const availW = w - padding.left - padding.right;
      const lineFactor = doc.getLineHeightFactor
        ? doc.getLineHeightFactor()
        : 1.15;
      const lineH = fontSize * lineFactor;

      doc.setFont("Roboto", "normal");
      const nameLines = name ? doc.splitTextToSize(name, availW) : [];
      const jabatanLines = jabatan ? doc.splitTextToSize(jabatan, availW) : [];

      const nameH = nameLines.length * lineH;
      const jabatanH = jabatanLines.length * lineH;

      // Bottom-anchored jabatan (top of jabatan block)
      const jabatanTop = y + h - padding.bottom - jabatanH;

      // Compute name top:
      // - Ideal (driven by headerNameGap) = headerY + headerNameGap
      // - Max allowed (to avoid collision) = jabatanTop - nameJabatanGap - nameH
      const idealNameTop = headerY + headerNameGap;
      const maxNameTop = jabatanTop - nameJabatanGap - nameH;

      // Place the name block as low as possible but not lower than allowed,
      // and at least 'headerNameGap' below the header.
      const nameTop = Math.min(maxNameTop, idealNameTop);

      // Draw wrapped text
      const textX = x + padding.left;
      if (nameLines.length)
        doc.text(nameLines, textX, nameTop, { align: "left" });
      if (jabatanLines.length)
        doc.text(jabatanLines, textX, jabatanTop, { align: "left" });
    };

    // Load logo
    const loadImageAsBase64 = (src) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = src;
        img.onload = () => {
          const cvs = document.createElement("canvas");
          cvs.width = img.width;
          cvs.height = img.height;
          const ctx = cvs.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(cvs.toDataURL("image/png"));
        };
        img.onerror = reject;
      });

    let iconBase64 = null;
    try {
      iconBase64 = await loadImageAsBase64(BgnIcon);
    } catch (_) {}

    // Start drawing
    let y = margin;

    // --- First title with logo
    drawCenteredTitle(
      "BERITA ACARA PENERIMAAN PAKET MAKANAN PROGRAM MAKAN BERGIZI GRATIS",
      y,
      iconBase64,
    );
    y += 30;

    // Narrative 1
    const qty = Number(qtyValue ?? 0);
    const tgl = fmtDate(orderDate);
    const hari = getDayName(orderDate);
    const lokasi = storedLocationName || "-";
    const lokasiHardcode = "Cisbinhar Arcamanik, Kota Bandung";
    const lokasiHardcode2 = "Arcamanik, Kota Bandung";
    const noHpNama = "0822-1088-9847 (Kepala SPPG: Pak Ryan)";
    const parag1 = `Pada hari ${hari} Tanggal ${tgl} pukul ${receivedTime || "__________"} telah diterima paket makanan sejumlah ${qty} ompreng Makanan Bergizi dari Satuan Pelayanan Pemenuhan Gizi (SPPG) ${kitchenData[0]?.address || ""} untuk dimakan paling lambat pukul __________ WIB`;
    y = drawParagraph(parag1, y + 36);
    doc.setFontSize(10);
    doc.text("Yang Menyerahkan : ", margin, y + 8);
    doc.text(`Contact Person : ${kitchenData[0].phone} (Kepala SPPG: ${kitchenData[0].kitchen_head})`, margin, y + 32);
    signatureLineBox("", margin, y + 3, 180, 90);
    y += 112;

    // Columns (first section)
    const colGap = 32;
    const colW = (pageWidth - margin * 2 - colGap) / 2;

    // LEFT side
    let leftX = margin;
    let leftY = y;
    doc.text(
      `Diterima oleh     : ${receivedBy || "____________________________"}`,
      leftX,
      leftY,
    );
    leftY += 32;
    doc.text(
      `Nomor Telepon : ${receivedPhone || "____________________________"}`,
      leftX,
      leftY,
    );
    leftY += 22;
    const leftEndY = signatureLineBox("", leftX, leftY, 180, 90); // returns y + h + 20
    const leftLineBottomY = leftEndY - 20; // actual baseline of the signature line

    // === QR in the middle between left signature line and right box (first section) ===
    if (qrCode) {
      const qrSize = 70;
      const gapStartX = margin + colW; // start of the middle gap
      const qrX = gapStartX + (colGap - qrSize) / 2;
      const qrY = leftLineBottomY - qrSize - 4; // align to the signature line

      try {
        const qrDataUrl = await QRCode.toDataURL(qrCode, {
          errorCorrectionLevel: "M",
        });
        doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
      } catch (err) {
        console.error("QR code generation failed (first section):", err);
      }
    }

    // RIGHT side
    let rightX = margin + colW + colGap;
    let rightY = y;
    doc.setFont("Roboto", "bold");
    doc.setFont("Roboto", "normal");
    const nama = "Ryan Putra Anjello, S.H.";
    const jabatan = "Kepala SPPG, " + kitchenData[0]?.address || "";
    const jabatan2 = "Kepala SPPG, " + lokasiHardcode;

    signatureBoxInside(rightX + colW - 190, rightY, 190, 120, {
      header: "Mengetahui:",
      jabatan,
      headerNameGap: 100, // try 48–60 to see larger gap
      padding: { top: 10, right: 5, bottom: 0, left: 5 },
      nameGap: 12,
    });
    rightY += 200;

    // --- Second title with logo
    y = Math.max(leftY, rightY) + 8;
    drawCenteredTitle(
      "BERITA ACARA PENGEMBALIAN ALAT MAKAN PROGRAM MAKAN BERGIZI GRATIS",
      y,
      iconBase64,
    );
    y += 30;

    // Narrative 2
    const parag2 = `Pada hari ${hari} Tanggal ${tgl} pukul __________  telah diserahkan kembali alat makan sejumlah ${qty} ompreng Makanan Bergizi dari Satuan Pelayanan Pemenuhan Gizi (SPPG) ${lokasiHardcode2}`;
    y = drawParagraph(parag2, y + 36);
    doc.setFontSize(10);
    doc.text("Yang Menyerahkan :____________________________", margin, y + 16);
    leftY += 40;
    doc.text(
      "Nomor Telepon       :____________________________",
      margin,
      y + 40,
    );
    signatureLineBox("", margin, y + 3, 180, 90);
    y += 112;

    // Columns (second section)
    leftX = margin;
    leftY = y;
    doc.setFont("Roboto", "normal");
    doc.text("Diterima oleh", leftX, leftY);
    leftY += 22;
    doc.text(`Contact Person : ${kitchenData[0].phone} (Kepala SPPG: ${kitchenData[0].kitchen_head})`, leftX, leftY);
    leftY += 12;
    // Get the returned pointer before modifying leftY further
    const leftEndY2 = signatureLineBox("", leftX, leftY, 180, 90);
    const leftLineBottomY2 = leftEndY2 - 20;

    // === QR in the middle between left signature line and right box (second section) ===
    if (qrCode) {
      const qrSize2 = 70;
      const gapStartX2 = margin + colW;
      const qrX2 = gapStartX2 + (colGap - qrSize2) / 2;
      const qrY2 = leftLineBottomY2 - qrSize2 - 4;

      try {
        const qrDataUrl2 = await QRCode.toDataURL(qrCode, {
          errorCorrectionLevel: "M",
        });
        doc.addImage(qrDataUrl2, "PNG", qrX2, qrY2, qrSize2, qrSize2);
      } catch (err) {
        console.error("QR code generation failed (second section):", err);
      }
    }

    // Continue layout on the left after signature line box
    leftY = leftEndY2;
    leftY += 30; // jarak ekstra bawah

    // RIGHT side (second section)
    rightX = margin + colW + colGap;
    rightY = y;
    doc.setFont("Roboto", "bold");
    doc.setFont("Roboto", "normal");
    rightY += 30;
    signatureBoxInside(rightX + colW - 190, rightY, 190, 120, {
      header: "Mengetahui:",
      jabatan,
      headerNameGap: 100, // try 48–60 to see larger gap
      padding: { top: 10, right: 5, bottom: 0, left: 5 },
      nameGap: 12,
    });

    // Footer
    doc.setFontSize(9);
    // doc.text("Hal. 1 / 1", pageWidth - margin, pageHeight - 16, { align: "right" });

    await appendEvaluationPage(
      doc,
      item,
      dataMenu,
      iconBase64,
      selectedDate.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }),
      qtyValue,
      deliveryLabel,
      kitchenData,
    );

    // Save
    const safeName = (schoolName || "Sekolah").replace(/[^\w\-]+/g, "_");
    const fileDate = new Date(orderDate).toISOString().slice(0, 10);
    doc.save(`Berita_Acara_${safeName}_${fileDate}.pdf`);
  };

  const [bulkPdf, setBulkPdf] = useState({ loading: false, done: 0, total: 0 });

  const handleDownloadAllPDFs = async () => {
    // Pick rows that have any qty > 0 to download
    const itemsToDownload = dataCustomers.filter((it) => {
      const pagi = toInt(formData[`qty_real_pagi_${it.id}`] ?? getBasePagi(it));
      const siang = toInt(
        formData[`qty_real_siang_${it.id}`] ?? getBaseSiang(it),
      );
      // Include items that either need to be saved OR are already saved
      return (
        (!it.real_pagi && pagi > 0) ||
        (it.real_pagi && pagi > 0) ||
        (!it.real_siang && siang > 0) ||
        (it.real_siang && siang > 0)
      );
    });

    // Count how many PDFs will be generated (pagi + siang separately)
    let total = 0;
    for (const it of itemsToDownload) {
      const pagi = toInt(formData[`qty_real_pagi_${it.id}`] ?? getBasePagi(it));
      const siang = toInt(
        formData[`qty_real_siang_${it.id}`] ?? getBaseSiang(it),
      );
      if (pagi > 0) total++; // Count both saved and unsaved
      if (siang > 0) total++;
    }
    if (total === 0) return;

    setBulkPdf({ loading: true, done: 0, total });

    for (const item of itemsToDownload) {
      const pagiQty = toInt(
        formData[`qty_real_pagi_${item.id}`] ?? getBasePagi(item),
      );
      const siangQty = toInt(
        formData[`qty_real_siang_${item.id}`] ?? getBaseSiang(item),
      );

      // Process PAGI: save if needed, then download
      if (pagiQty > 0) {
        const key = pdfKey(item.id, "pagi");
        setPdfStatus((s) => ({ ...s, [key]: "loading" }));

        try {
          // Save if not already saved
          if (!item.real_pagi) {
            const payload = {
              kitchen_id: parseInt(storedKitchenId, 10),
              date: localDateString(selectedDate),
              admin_id: storedUserId,
              customer_id: [item.id],
              qty_real_pagi: [pagiQty],
              qty_sch_pagi: [pagiQty],
            };
            await instance2.post(`/kitchen_result`, payload);
          }

          // Fetch updated data to get QR code
          const res = await instance2.get(
            `/customers/result/${storedKitchenId || kitchenIdKoperasi}/${localDateString(selectedDate)}`,
          );
          const rows = Array.isArray(res.data) ? res.data : [];
          const normalized = rows.map((r) => ({
            ...r,
            qty_sch: toInt(r.qty_sch),
            qty_sch_pagi: toInt(r.qty_sch_pagi),
            qty_sch_siang: toInt(r.qty_sch_siang),
            qty_real: toInt(r.qty_real),
            qty_real_pagi: toInt(r.qty_real_pagi),
            qty_real_siang: toInt(r.qty_real_siang),
          }));

          // Update state with fresh data
          setDataCustomers(normalized);

          const updatedRow = normalized.find((r) => r.id === item.id) || item;

          // Generate PDF
          await generatePDFBaru(
            updatedRow,
            updatedRow.customer_name,
            selectedDate,
            pagiQty,
            del1Label,
            updatedRow.qr_pagi,
            "pagi",
          );

          setBulkPdf((s) => ({ ...s, done: s.done + 1 }));
        } catch (e) {
          console.error("Bulk PDF error (pagi):", e);
        } finally {
          setPdfStatus((s) => ({ ...s, [key]: "idle" }));
        }

        // Small delay between downloads
        await new Promise((r) => setTimeout(r, 100));
      }

      // Process SIANG: save if needed, then download
      if (siangQty > 0) {
        const key = pdfKey(item.id, "siang");
        setPdfStatus((s) => ({ ...s, [key]: "loading" }));

        try {
          // Save if not already saved
          if (!item.real_siang) {
            const payload = {
              kitchen_id: parseInt(storedKitchenId, 10),
              date: localDateString(selectedDate),
              admin_id: storedUserId,
              customer_id: [item.id],
              qty_real_siang: [siangQty],
              qty_sch_siang: [siangQty],
            };
            await instance2.post(`/kitchen_result`, payload);
          }

          // Fetch updated data to get QR code
          const res = await instance2.get(
            `/customers/result/${storedKitchenId || kitchenIdKoperasi}/${localDateString(selectedDate)}`,
          );
          const rows = Array.isArray(res.data) ? res.data : [];
          const normalized = rows.map((r) => ({
            ...r,
            qty_sch: toInt(r.qty_sch),
            qty_sch_pagi: toInt(r.qty_sch_pagi),
            qty_sch_siang: toInt(r.qty_sch_siang),
            qty_real: toInt(r.qty_real),
            qty_real_pagi: toInt(r.qty_real_pagi),
            qty_real_siang: toInt(r.qty_real_siang),
          }));

          // Update state with fresh data
          setDataCustomers(normalized);

          const updatedRow = normalized.find((r) => r.id === item.id) || item;

          // Generate PDF
          await generatePDFBaru(
            updatedRow,
            updatedRow.customer_name,
            selectedDate,
            siangQty,
            del2Label,
            updatedRow.qr_siang,
            "siang",
          );

          setBulkPdf((s) => ({ ...s, done: s.done + 1 }));
        } catch (e) {
          console.error("Bulk PDF error (siang):", e);
        } finally {
          setPdfStatus((s) => ({ ...s, [key]: "idle" }));
        }

        // Small delay between downloads
        await new Promise((r) => setTimeout(r, 100));
      }
    }

    setBulkPdf((s) => ({ ...s, loading: false }));
  };

  // Custom Input for Date Picker
  const CustomInput = React.forwardRef(({ value, onClick, isOpen }, ref) => (
    <button
      className={`flex items-center justify-between w-full px-4 py-3 border rounded-full ${startDate ? "bg-gray-200 cursor-not-allowed" : "bg-white"}`}
      onClick={onClick}
      ref={ref}
      type="button"
    >
      <div className="flex items-center gap-2">
        <img
          src={CalendarIcon}
          alt="Calendar"
          className="h-5 w-5 text-gray-500"
        />
        <div className="w-px h-5 bg-gray-300 mx-1 me-3"></div>
        <span>
          {new Date(value).toLocaleDateString("id-ID", {
            year: "numeric",
            month: "long",
            day: "2-digit",
          })}
        </span>
      </div>
      <div
        className={`text-black font-semibold transform transition-transform duration-500 ease-in-out ${
          isOpen ? "rotate-180" : "rotate-0"
        }`}
      >
        ▼
      </div>
    </button>
  ));

  const del1Label = "PAGI";
  const del2Label = "SIANG";

  const getTotalQtyPagi = () =>
    dataCustomers.reduce((sum, item) => {
      const val =
        formData[`qty_pagi_${item.id}`] !== undefined
          ? parseInt(formData[`qty_pagi_${item.id}`], 10)
          : item.qty_pagi || 0;
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

  const getTotalQtySch = () =>
    dataCustomers.reduce((sum, it) => sum + (parseInt(it.qty_sch, 10) || 0), 0);
  const getRowTotal = (item) =>
    toInt(formData[`qty_real_pagi_${item.id}`] ?? getBasePagi(item)) +
    toInt(formData[`qty_real_siang_${item.id}`] ?? getBaseSiang(item));

  const getTotalQtyRealPagi = () =>
    dataCustomers.reduce(
      (sum, it) =>
        sum + toInt(formData[`qty_real_pagi_${it.id}`] ?? getBasePagi(it)),
      0,
    );

  const getTotalQtyRealSiang = () =>
    dataCustomers.reduce(
      (sum, it) =>
        sum + toInt(formData[`qty_real_siang_${it.id}`] ?? getBaseSiang(it)),
      0,
    );

  const getTotalQtyReal = () => getTotalQtyRealPagi() + getTotalQtyRealSiang();

  const getTotalQtySiang = () =>
    dataCustomers.reduce((sum, item) => {
      const val =
        formData[`qty_siang_${item.id}`] !== undefined
          ? parseInt(formData[`qty_siang_${item.id}`], 10)
          : item.qty_siang || 0;
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

  // choose which numbers to show/use by default
  const UseRealQty = (it) => Boolean(it?.real);

  // pick default numbers shown/used for each session
  const UseRealPagi = (it) => Boolean(it?.real_pagi);
  const UseRealSiang = (it) => Boolean(it?.real_siang);

  const getBasePagi = (it) =>
    UseRealPagi(it) ? toInt(it.qty_real_pagi) : toInt(it.qty_sch_pagi);
  const getBaseSiang = (it) =>
    UseRealSiang(it) ? toInt(it.qty_real_siang) : toInt(it.qty_sch_siang);

  // track row/session saving states
  // helpers
  const keyOf = (id, sess) => `${id}-${sess}`;
  const [requestStatus, setRequestStatus] = useState({});
  const isSaving = (id, sess) => requestStatus[keyOf(id, sess)] === "loading";

  const saveOne = async (row, session) => {
    const idKey = keyOf(row.id, session);
    setRequestStatus((s) => ({ ...s, [idKey]: "loading" }));

    const value =
      session === "pagi"
        ? toInt(formData[`qty_real_pagi_${row.id}`] ?? getBasePagi(row))
        : toInt(formData[`qty_real_siang_${row.id}`] ?? getBaseSiang(row));

    const payload = {
      kitchen_id: parseInt(storedKitchenId, 10),
      date: localDateString(selectedDate),
      admin_id: storedUserId,
      customer_id: [row.id],
      ...(session === "pagi"
        ? { qty_real_pagi: [value], qty_sch_pagi: [value] }
        : { qty_real_siang: [value], qty_sch_siang: [value] }),
    };

    try {
      await instance2.post(`/kitchen_result`, payload);

      const res = await instance2.get(
        `/customers/result/${storedKitchenId || kitchenIdKoperasi}/${localDateString(selectedDate)}`,
      );

      const rows = Array.isArray(res.data) ? res.data : [];
      const normalized = rows.map((r) => ({
        ...r,
        qty_sch: toInt(r.qty_sch),
        qty_sch_pagi: toInt(r.qty_sch_pagi),
        qty_sch_siang: toInt(r.qty_sch_siang),
        qty_real: toInt(r.qty_real),
        qty_real_pagi: toInt(r.qty_real_pagi),
        qty_real_siang: toInt(r.qty_real_siang),
      }));

      setDataCustomers(normalized);
      setRequestStatus((s) => ({ ...s, [idKey]: "done" }));

      const updatedRow = normalized.find((r) => r.id === row.id) || row;
      await downloadOnePDF(updatedRow, session); // 👈 This already passes session
    } catch (err) {
      console.error(err);
      setRequestStatus((s) => ({ ...s, [idKey]: "error" }));
    }
  };

  const anySaved = dataCustomers.some((it) => it.real_pagi || it.real_siang);

  return (
    <div className="p-4">
      {/* Back Button & Header */}
      <div className="flex flex-row gap-4 items-center justify-between ">
        <div className="flex flex-row gap-4 items-center">
          <div
            className="bg-white p-2 rounded-full cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <BackButton className="rotate-180 text-black" />
          </div>
          <h1 className="text-xl sm:text-2xl font-medium">
            {startDate ? "Download Berita Acara" : "Tambah Data Produksi"}
          </h1>
        </div>

        {(roleId === 3 || roleId === 4 || roleId === 5) && formData.qty_real ? (
          <div className="flex flex-row gap-4 items-center">
            <h1 className="text-xl sm:text-2xl font-medium">
              {"Cluster Produksi Harian"}
            </h1>
            <div
              className="bg-white p-2 rounded-full cursor-pointer"
              onClick={() =>
                navigate(
                  `/detailproduksiharian?date=${localDateString(new Date(startDate))}&kitchenId=${storedKitchenId || kitchenIdKoperasi}`,
                )
              }
            >
              <BackButton className="text-black" />
            </div>
          </div>
        ) : null}
      </div>
      {/* Loading & Error Handling */}
      {loading ? (
        <div className="px-4 py-8 flex justify-center">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <div className="py-5">
          {/* Form Section */}
          <div className="flex flex-col gap-6 lg:flex-row justify-between items-start">
            {/* Left Column */}
            <div className="flex flex-col gap-4 w-full lg:w-1/2">
              {/* Date Picker */}
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <label className="block text-gray-700 w-full sm:w-1/4 text-sm">
                  Tanggal
                </label>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => {
                    setSelectedDate(date);
                    setFormData((prev) => ({
                      ...prev,
                      date: date.toISOString().split("T")[0],
                    }));
                  }}
                  customInput={
                    <CustomInput
                      value={selectedDate.toLocaleDateString("id-ID", {
                        year: "numeric",
                        month: "long",
                        day: "2-digit",
                      })}
                      isOpen={isOpen}
                    />
                  }
                  onCalendarOpen={() => setIsOpen(true)}
                  onCalendarClose={() => setIsOpen(false)}
                  dateFormat="yyyy-MM-dd"
                  filterDate={(date) =>
                    date.getDay() !== 0 && date.getDay() !== 6
                  }
                  disabled={!!startDate}
                />
              </div>

              {/* Kitchen Name */}
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <label className="block text-gray-700 w-full sm:w-1/4 text-sm">
                  Nama Dapur
                </label>
                <input
                  type="text"
                  name="name"
                  value={kitchenName}
                  className="w-full px-3 border border-gray-300 rounded-lg text-xs py-2 bg-gray-100"
                  disabled
                />
              </div>

              {/* Cooperative Name */}
              {/* <div className="flex flex-col sm:flex-row gap-4 items-center">
                <label className="block text-gray-700 w-full sm:w-1/4 text-sm">
                  Koperasi
                </label>
                <input
                  type="text"
                  name="description"
                  value={storedLocationName}
                  className="w-full px-3 border border-gray-300 rounded-lg text-xs py-2 bg-gray-100"
                  disabled
                />
              </div> */}
            </div>

            {/* Right Column: Menu Schedule */}
            <div className="w-full lg:w-1/2 border border-black rounded-lg p-4">
              <div className="text-center font-semibold text-sm mb-2">
                Menu Hari Ini
              </div>
              {dataMenu ? (
                <div className="flex flex-col gap-2 text-center text-sm text-gray-700">
                  <div>{dataMenu.KARBOHIDRAT}</div>
                  <div>{dataMenu.PROTEIN}</div>
                  <div>{dataMenu.SAYURAN}</div>
                  <div>{dataMenu.BUAH}</div>
                  <div>{dataMenu.MINUMAN}</div>
                </div>
              ) : (
                <div className="text-center text-sm text-gray-500">
                  Belum ada data
                </div>
              )}
            </div>
          </div>

          {!checkingMenuStatus && !isMenuSaved && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
              <p className="text-yellow-800 text-sm">
                Menu belum disimpan. Silakan simpan menu terlebih dahulu untuk
                mengunduh berita acara.
              </p>
            </div>
          )}
          {/* Table for Customer Data */}
          <div className="mt-6 w-full items-center text-center justify-between">
            <h2 className="text-lg font-semibold mb-2">Realisasi</h2>
            <table className="table-fixed border-gray-300 mx-auto">
              {/* Table Header */}
              <thead className="bg-white">
                <tr className="text-left text-sm text-gray-700">
                  <th className="border-b border-gray-300 px-4 py-2 rounded-tl-[15px] text-left text-xxs md:text-xxs lg:text-base">
                    Sekolah
                  </th>
                  <th className="border-b border-gray-300 px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                    Rencana
                  </th>
                  <th className="border-b border-gray-300 px-4 py-2 text-center text-xxs md:text-xxs lg:text-base">
                    {/* Realisasi  */}
                    {del1Label}
                  </th>
                  <th className="border-b border-gray-300 px-4 py-2 text-center text-xxs md:text-xxs lg:text-base">
                    {/* Realisasi  */}
                    {del2Label}
                  </th>
                  <th className="border-b border-gray-300 px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                    Total Realisasi
                  </th>
                  <th className="border-b border-gray-300 px-4 py-2 rounded-tr-[15px] text-center text-xxs md:text-xxs lg:text-base">
                    Aksi
                  </th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {dataCustomers.map((item, index) => (
                  <tr
                    key={item.id ?? index}
                    className={index % 2 === 0 ? "bg-transparent" : "bg-white"}
                  >
                    {/* School */}
                    <td className="px-4 py-2 text-left text-xxs md:text-xxs lg:text-base">
                      {item.customer_name}
                    </td>

                    {/* Rencana (combined) */}
                    <td className="px-4 py-2 text-right pe-7 text-xxs md:text-xxs lg:text-base">
                      {formatNumber(parseInt(item.qty_sch, 10) || 0)}
                    </td>

                    {/* Realisasi PAGI */}
                    <td className="px-4 py-2 text-right align-top">
                      <div className="flex flex-col gap-2 items-start">
                        {(() => {
                          const pagiQty = toInt(
                            formData[`qty_real_pagi_${item.id}`] ??
                              getBasePagi(item),
                          );

                          return (
                            <>
                              <input
                                type="text"
                                className="w-full px-3 py-2 border rounded-lg text-right text-xxs md:text-xxs lg:text-base"
                                value={formatNumber(pagiQty)}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/\./g, "");
                                  setFormData((prev) => ({
                                    ...prev,
                                    [`qty_real_pagi_${item.id}`]: toInt(raw),
                                  }));
                                }}
                                disabled
                              />

                              {/* Show buttons only if menu is saved */}
                              {isMenuSaved && (
                                <>
                                  {/* Show SAVE/DOWNLOAD only when qty > 0 */}
                                  {!item.real_pagi && pagiQty > 0 && (
                                    <button
                                      className="bg-oren-no text-white px-3 py-1 rounded hover:bg-oren-hover text-xxs md:text-xxs lg:text-base w-full disabled:opacity-60"
                                      onClick={() => saveOne(item, "pagi")}
                                      disabled={isSaving(item.id, "pagi")}
                                    >
                                      {isPdfLoading(item.id, "pagi") ? (
                                        <span className="inline-flex items-center gap-2">
                                          <span className="inline-block w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                                          Mengunduh...
                                        </span>
                                      ) : (
                                        "Download Berita Acara"
                                      )}
                                    </button>
                                  )}

                                  {item.real_pagi && pagiQty > 0 && (
                                    <button
                                      type="button"
                                      className="bg-merah-no text-white px-3 py-1 rounded hover:bg-merah-hover text-xxs md:text-xxs lg:text-base w-full disabled:opacity-60"
                                      onClick={() =>
                                        downloadOnePDF(item, "pagi")
                                      }
                                      disabled={
                                        isPdfLoading(item.id, "pagi") ||
                                        bulkPdf.loading
                                      }
                                    >
                                      {isPdfLoading(item.id, "pagi") ? (
                                        <span className="inline-flex items-center gap-2">
                                          <span className="inline-block w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                                          Mengunduh...
                                        </span>
                                      ) : (
                                        "Download Berita Acara"
                                      )}
                                    </button>
                                  )}
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </td>

                    {/* Realisasi SIANG */}
                    <td className="px-4 py-2 text-right align-top">
                      <div className="flex flex-col gap-2 items-start">
                        {(() => {
                          const siangQty = toInt(
                            formData[`qty_real_siang_${item.id}`] ??
                              getBaseSiang(item),
                          );

                          return (
                            <>
                              <input
                                type="text"
                                className="w-full px-3 py-2 border rounded-lg text-right text-xxs md:text-xxs lg:text-base"
                                value={formatNumber(siangQty)}
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/\./g, "");
                                  setFormData((prev) => ({
                                    ...prev,
                                    [`qty_real_siang_${item.id}`]: toInt(raw),
                                  }));
                                }}
                                disabled
                              />

                              {/* Show buttons only if menu is saved */}
                              {isMenuSaved && (
                                <>
                                  {/* Show SAVE/DOWNLOAD only when qty > 0 */}
                                  {!item.real_siang && siangQty > 0 && (
                                    <button
                                      className="bg-oren-no text-white px-3 py-1 rounded hover:bg-oren-hover text-xxs md:text-xxs lg:text-base w-full disabled:opacity-60"
                                      onClick={() => saveOne(item, "siang")}
                                      disabled={isSaving(item.id, "siang")}
                                    >
                                      {isPdfLoading(item.id, "siang") ? (
                                        <span className="inline-flex items-center gap-2">
                                          <span className="inline-block w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                                          Mengunduh...
                                        </span>
                                      ) : (
                                        "Download Berita Acara"
                                      )}
                                    </button>
                                  )}

                                  {item.real_siang && siangQty > 0 && (
                                    <button
                                      type="button"
                                      className="bg-merah-no text-white px-3 py-1 rounded hover:bg-merah-hover text-xxs md:text-xxs lg:text-base w-full disabled:opacity-60"
                                      onClick={() =>
                                        downloadOnePDF(item, "siang")
                                      }
                                      disabled={
                                        isPdfLoading(item.id, "siang") ||
                                        bulkPdf.loading
                                      }
                                    >
                                      {isPdfLoading(item.id, "siang") ? (
                                        <span className="inline-flex items-center gap-2">
                                          <span className="inline-block w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                                          Mengunduh...
                                        </span>
                                      ) : (
                                        "Download Berita Acara"
                                      )}
                                    </button>
                                  )}
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </td>

                    {/* Total Realisasi */}
                    <td className="px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                      {formatNumber(getRowTotal(item))}
                    </td>

                    {/* Aksi: Download semua for this row? (optional keep global button below) */}
                    <td className="px-4 py-2 text-center text-xxs md:text-xxs lg:text-base">
                      {/* leave empty or add per-row action */}
                    </td>
                  </tr>
                ))}

                {/* Totals row */}
                <tr>
                  <td className="text-xxs md:text-xxs lg:text-base">Total</td>

                  <td className="px-4">
                    <input
                      type="text"
                      name="qty_sch_total"
                      value={formatNumber(getTotalQtySch())}
                      className="w-full text-right px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-xxs md:text-xxs lg:text-base"
                      disabled
                    />
                  </td>

                  <td className="px-4">
                    <input
                      type="text"
                      name="qty_real_pagi_total"
                      value={formatNumber(getTotalQtyRealPagi())}
                      className="w-full text-right px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-xxs md:text-xxs lg:text-base"
                      disabled
                    />
                  </td>

                  <td className="px-4">
                    <input
                      type="text"
                      name="qty_real_siang_total"
                      value={formatNumber(getTotalQtyRealSiang())}
                      className="w-full text-right px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-xxs md:text-xxs lg:text-base"
                      disabled
                    />
                  </td>

                  <td className="px-4">
                    <input
                      type="text"
                      name="qty_real_total"
                      value={formatNumber(getTotalQtyReal())}
                      className="w-full text-right px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-xxs md:text-xxs lg:text-base"
                      disabled
                    />
                  </td>

                  <td>
                    {/* {anySaved && ( */}
                    <td>
                      {isMenuSaved && (
                        <div className="mt-3 flex justify-end">
                          <button
                            className="bg-oren-no hover:bg-oren-hover text-white py-2 px-4 rounded text-xxs md:text-xxs lg:text-base disabled:opacity-60"
                            onClick={handleDownloadAllPDFs}
                            disabled={bulkPdf.loading}
                          >
                            {bulkPdf.loading ? (
                              <span className="inline-flex items-center gap-2">
                                <span className="inline-block w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                                Mengunduh Semua… ({bulkPdf.done}/{bulkPdf.total}
                                )
                              </span>
                            ) : (
                              "Download Semua PDF"
                            )}
                          </button>
                        </div>
                      )}
                    </td>
                    {/* )} */}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Buttons */}
          <div className="flex justify-center mt-6 gap-5">
            <button
              className="bg-merah-no text-white py-3 px-6 rounded-lg hover:bg-merah-hover text-xxs md:text-xxs lg:text-base"
              onClick={() => navigate(-1)}
            >
              Kembali
            </button>

            {/* {(formData.qty_real === null || formData.qty_real === "") && (
              <button
                onClick={handleSubmit}
                disabled={!isFormValid()}
                className={`py-3 px-6 rounded-lg text-xxs md:text-xxs lg:text-base ${
                  isFormValid()
                    ? "bg-merah-no text-white hover:bg-merah-hover"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Simpan
              </button>
             )}  */}
          </div>
        </div>
      )}
    </div>
  );
}
